// src/components/pages/allocation/partial/ResultChart.js

import { useState, useEffect, useRef, useCallback } from "react"; // ✅ useCallback 임포트
import Plot from "react-plotly.js";
import { useTranslation } from "react-i18next";
import chartStyles from "./style/ResultChart.module.css";
import commonStyles from "../../../../styles/Common.module.css";
import DataPeriodAlert from "../../backtest/partials/DataPeriodAlert"
import inputStyles from "./style/PortfolioInput.module.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

function ResultChart({
  stocks,
  optimizeType,
  riskFreeRate,
  userViews,
  onWeightsChange,
  allowShortSelling,
  onBacktest,
  dataPeriod,
  startDate,
  endDate,
  backtestPeriod,
  setBacktestPeriod,
  backtestStartDate,
  setBacktestStartDate,
  backtestEndDate,
  setBacktestEndDate,
  isBacktestCustomPeriod,
  setIsBacktestCustomPeriod,
}) {
  const [data, setData] = useState({ weights: [] });
  const [manualWeights, setManualWeights] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRefs = useRef({});
  const debounceTimeoutRef = useRef(null); 
  const manualWeightsRef = useRef({}); // ✅ 추가: 가장 최신 입력된 비중 값을 저장할 Ref
  const { t } = useTranslation();

  const today = new Date();
  const tenYearsAgo = new Date(new Date().setFullYear(today.getFullYear() - 30));

  const normalizeWeights = (weights) => {
    const totalAbsWeight = Object.values(weights).reduce((acc, val) => acc + Math.abs(parseFloat(val) || 0), 0);
    if (totalAbsWeight === 0) return weights;
    const normalized = {};
    Object.keys(weights).forEach(stock => {
      normalized[stock] = ((parseFloat(weights[stock]) || 0) / totalAbsWeight * 100).toFixed(1);
    });
    return normalized;
  };
  
  // ✅ 추가: 비중 정규화 및 상위 컴포넌트 업데이트 로직을 분리
  const applyNewWeights = useCallback((weightsToNormalize) => {
      const normalized = normalizeWeights(weightsToNormalize);
      setManualWeights(normalized);
      onWeightsChange(normalized);
  }, [onWeightsChange]);


  useEffect(() => {
    if (stocks.length > 0 && optimizeType) {
      setIsLoading(true);
      setError(null);
      let url = `/.netlify/functions/allocate?stocks=${stocks.join(",")}&optimizeType=${optimizeType}&riskFreeRate=${riskFreeRate}`;
      if (dataPeriod) {
        url += `&dataPeriod=${dataPeriod}`;
      } else if (startDate && endDate) {
        url += `&startDate=${format(startDate, 'yyyy-MM-dd')}&endDate=${format(endDate, 'yyyy-MM-dd')}`;
      }
      if ((optimizeType === "blackLiterman" || optimizeType === "maxSharpe") && userViews) {
        url += `&userViews=${encodeURIComponent(JSON.stringify(userViews))}`;
      }
      url += `&allowShortSelling=${allowShortSelling}`;

      fetch(url)
        .then(async res => {
          if (!res.ok) {
            const resultData = await res.json().catch(() => ({}));
            const dateParams = startDate && endDate ? { startDate: format(startDate, 'yyyy-MM-dd'), endDate: format(endDate, 'yyyy-MM-dd') } : {};
            let errorMessage = res.status === 400
              ? t("backtest.error.dataMissingStrict3", { startDate: dateParams.startDate || 'N/A', endDate: dateParams.endDate || 'N/A' })
              : res.status === 500
                ? t("chart.serverError")
                : resultData.error || t("chart.fetchError");
            throw new Error(errorMessage);
          }
          return res.json();
        })
        .then(data => {
          setData(data);
          let updatedWeights = {};
          stocks.forEach((stock, index) => {
            updatedWeights[stock] = (data.weights[index] * 100).toFixed(1);
          });
          updatedWeights = normalizeWeights(updatedWeights);
          setManualWeights(updatedWeights);
          manualWeightsRef.current = updatedWeights; // ✅ Ref 초기화
          onWeightsChange(updatedWeights);
        })
        .catch(error => {
          setError(error.message);
          setData({ weights: [] });
          setManualWeights({});
        })
        .finally(() => setIsLoading(false));
    }
  }, [stocks, optimizeType, riskFreeRate, userViews, allowShortSelling, dataPeriod, startDate, endDate, onWeightsChange, t]);

  // ✅ 수정: Ref를 사용하여 최신 값을 추적
  const handleWeightChange = (stock, value) => {
    // 1. Ref와 State에 업데이트된 비중 저장 (Ref는 최신, State는 UI 표시용)
    const updatedWeights = { ...manualWeightsRef.current, [stock]: parseFloat(value) || 0 };
    manualWeightsRef.current = updatedWeights;
    setManualWeights(updatedWeights); 
    
    // 2. 기존 타이머 클리어
    if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
    }

    // 3. 500ms 디바운스 설정: 입력이 멈춘 후 0.5초 뒤에 applyNewWeights 호출
    debounceTimeoutRef.current = setTimeout(() => {
        applyNewWeights(manualWeightsRef.current);
        debounceTimeoutRef.current = null;
    }, 500);
  };

  const startContinuousChange = (stock, delta) => {
    // 1. Ref와 State에 업데이트
    setManualWeights(prev => {
      const currentValue = parseFloat(prev[stock]) || 0;
      const newWeights = { ...prev, [stock]: currentValue + delta };
      manualWeightsRef.current = newWeights; // ✅ Ref 업데이트
      return newWeights;
    });
    
    // 2. Interval로 연속 업데이트
    intervalRefs.current[stock] = setInterval(() => {
      setManualWeights(prev => {
        const currentValue = parseFloat(prev[stock]) || 0;
        const newWeights = { ...prev, [stock]: currentValue + delta };
        manualWeightsRef.current = newWeights; // ✅ Ref 업데이트
        return newWeights;
      });
    }, 100);
  };

  const stopContinuousChange = (stock) => {
    clearInterval(intervalRefs.current[stock]);
    // ✅ 수정 없음: 버튼 변경이 완료되면 즉시 Allocation.js로 전달
    setTimeout(() => {
      // Ref에 저장된 최종 값을 사용하여 정규화 및 업데이트
      applyNewWeights(manualWeightsRef.current); 
    }, 100);
  };

  // ✅ 수정: 백테스트 클릭 시, Debounce 타이머 강제 실행
  const handleBacktestClick = () => {
    // 1. Debounce 타이머가 활성화되어 있으면 즉시 강제 실행 (비중 업데이트)
    if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
        applyNewWeights(manualWeightsRef.current); // 최신 입력 값을 Allocation.js로 전달
    }
    
    // 2. 백테스트 시작 (최신 비중이 Allocation.js에 반영된 상태)
    onBacktest();
    setTimeout(() => document.getElementById('backtest-section')?.scrollIntoView({ behavior: "smooth" }), 1000);
  };

  const normalColors = Array.from({ length: 100 }, (_, i) => `hsl(${(i * 137.5) % 360}, 70%, 50%)`);
  const markerColors = stocks.map((_, i) => normalColors[i % normalColors.length]);
  const labels = stocks.map(stock => {
    const weight = parseFloat(manualWeights[stock]) || 0;
    return weight < 0 ? `(${t("short")})<br>${stock}` : stock;
  });
  const values = stocks.map(stock => Math.abs(parseFloat(manualWeights[stock]) || 0));

  return (
    <div className={chartStyles['portfolio-plot']}>
      {data.period && (
        <p>
          {data.period.start}~{data.period.end} ({data.period.days} days)
        </p>
      )}

      
      {/* <DataPeriodAlert 
            dataPeriodInfo={data.dataPeriodInfo} 
            t={t}
        
      /> */}

      {data.dataPeriodInfo && data.dataPeriodInfo.isPeriodLimited && (
        <DataPeriodAlert 
            dataPeriodInfo={data.dataPeriodInfo} 
            t={t}
        />
      )}
      <div className={chartStyles['chart-container']}>
        {isLoading ? (
          <div className={commonStyles.loadingContainer}>
            <div className={commonStyles.spinner}></div>
            {t("dataLoadingCalculatiing")}
          </div>
        ) : error ? (
          <div className={commonStyles.loadingContainer} style={{ padding: "20px", textAlign: "center", color: "#d32f2f", border: "1px solid #d32f2f", borderRadius: "8px" }}>
            <strong>❌ {t("backtest.error.calculationFailed")}</strong>
            <p style={{ marginTop: "10px", wordBreak: "break-word" }}>{error}</p>
            <p style={{ marginTop: "10px", fontSize: "0.9em", color: "#757575" }}>{t("backtest.error.checkInputsOrData")}</p>
          </div>
        ) : (
          <Plot
            data={[{
              labels,
              values,
              type: "pie",
              textinfo: "label+percent",
              textposition: "outside",
              marker: { colors: markerColors },
              hoverinfo: "label+percent+value",
              hole: 0.4,
            }]}
            layout={{
              title: t("optimalPortfolioWeight"),
              autosize: true,
              showlegend: false,
            }}
            config={{ responsive: true }}
            className={chartStyles['pie-chart']}
          />
        )}
      </div>

      <div className={chartStyles['manual-weight-inputs']}>
        {stocks.map(stock => {
          const isShort = parseFloat(manualWeights[stock]) < 0;
          return (
            <div key={stock} className={chartStyles['manual-weight-item']}>
              <label style={{ color: isShort ? "#2563EB" : "black" }}>{stock}</label>
              <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  onMouseDown={() => startContinuousChange(stock, -0.5)}
                  onMouseUp={() => stopContinuousChange(stock)}
                  onMouseLeave={() => stopContinuousChange(stock)}
                  onTouchStart={() => startContinuousChange(stock, -0.5)}
                  onTouchEnd={() => stopContinuousChange(stock)}
                >
                  -
                </button>
                <input
                  type="number"
                  value={manualWeights[stock] || ""}
                  onChange={e => handleWeightChange(stock, e.target.value)}
                  step="0.5"
                  min="-100"
                  inputMode="numeric"
                  style={{ textAlign: "center", width: "60px" }}
                />
                <button
                  onMouseDown={() => startContinuousChange(stock, 0.5)}
                  onMouseUp={() => stopContinuousChange(stock)}
                  onMouseLeave={() => stopContinuousChange(stock)}
                  onTouchStart={() => startContinuousChange(stock, 0.5)}
                  onTouchEnd={() => stopContinuousChange(stock)}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className={inputStyles['data-period']} style={{ margin: "20px 0 0" }}>
        <span>{t("backtestPeriod")}</span>
        <select
          value={isBacktestCustomPeriod ? "custom" : backtestPeriod}
          onChange={e => {
            const value = e.target.value;
            setIsBacktestCustomPeriod(value === "custom");
            if (value !== "custom") {
              setBacktestPeriod(parseInt(value, 10));
              setBacktestStartDate(tenYearsAgo);
              setBacktestEndDate(today);
            }

            if (value === "custom") {
              if (!backtestStartDate) setBacktestStartDate(tenYearsAgo);
              if (!backtestEndDate) setBacktestEndDate(today);
            }
          }}
        >
          {Array.from({ length: 10 }, (_, i) => i + 1).map(year => (
            <option key={year} value={year}>{year} {t("year")}</option>
          ))}
          {/* 20년, 30년 옵션 추가 */}
          {[20, 30].map(year => (
              <option key={year} value={year}>{t(`backtest.${year}years`)}</option>
          ))}

          <option value="custom">{t("backtest.custom")}</option>
        </select>
      </div>
      <br></br>
      {isBacktestCustomPeriod && (
        <div className={inputStyles['custom-date-pickers']}>
          <DatePicker
            selected={backtestStartDate}
            onChange={setBacktestStartDate}
            selectsStart
            startDate={backtestStartDate}
            endDate={backtestEndDate}
            dateFormat="yyyy-MM-dd"
            minDate={tenYearsAgo}
            maxDate={backtestEndDate || today}
            placeholderText="YYYY-MM-DD"
            className={inputStyles['date-input']}
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
          />
          <span> ~ </span>
          <DatePicker
            selected={backtestEndDate}
            onChange={setBacktestEndDate}
            selectsEnd
            startDate={backtestStartDate}
            endDate={backtestEndDate}
            minDate={backtestStartDate || tenYearsAgo}
            maxDate={today}
            dateFormat="yyyy-MM-dd"
            placeholderText="YYYY-MM-DD"
            className={inputStyles['date-input']}
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
          />
        </div>
      )}

      <div className={inputStyles['button-container']}>
        <button className={inputStyles['calculate-button']} onClick={handleBacktestClick}>
          {t("dobacktest")}
        </button>
      </div>
    </div>
  );
}

export default ResultChart;